import { useMemo, useState, type ReactNode, type Ref, type UIEventHandler } from 'react';
import { styled } from '@mui/material/styles';

import { notchMask } from '../../components/notch';
import type { Scope } from '../../components/viewToggleMetrics';
import MobileEventCard from '../explore/MobileEventCard';
import type { LoosenSuggestion } from '../explore/filters';
import type { Opportunity } from '../../shared/opportunity';
import { isPastEvent, type Moment } from '../../shared/pastEvents';
import { longDate } from '../../shared/taxonomy';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { useIsMobile } from '../../shared/useIsMobile';
import type { RsvpStatus } from '../../shared/useRsvp';
import DotCalendar from './DotCalendar';
import MonthCalendar from './MonthCalendar';
import {
  bucketByDay,
  defaultDay,
  inMonth,
  monthKey,
  monthName,
  monthOf,
  monthTitle,
  nearestBusyMonth,
  shiftMonth,
  type Month,
} from './month';

/**
 * The Calendar layout, in either scope (contract §10): a month of whatever the
 * filters leave — every matching date, a series on each of its days — with
 * Going and Maybe tinted as their tickets are.
 *
 * From 600px up, the month as a grid of chips, "+N more" opening a day in
 * place; on a phone, a dot calendar with the chosen day's tickets under it. A
 * chip or a ticket opens the callout in place: beside the month where it fits,
 * over it where it doesn't, and never instead of it. The page around it — the
 * sidebars, the top strip, the callout's column — is the same one the list is
 * drawn in.
 */

const Scroll = styled('div')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  scrollbarGutter: 'stable',
  padding: '0 28px 96px',
  // Below 900px a sideways swipe here is the page's, to the other scope.
  [theme.breakpoints.down('md')]: { padding: '0 0 72px', touchAction: 'pan-y' },
  // A phone: the tickets reach the screen's edges, as the list's do, and the
  // last one clears the toggle's band.
  [theme.breakpoints.down('sm')]: {
    padding: '0 0 calc(24px + var(--toggle-band, 0px))',
    overflowX: 'hidden',
  },
}));

const Inner = styled('div')({ width: '100%', maxWidth: 1180, margin: '0 auto' });

const Bar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 0 14px',
  [theme.breakpoints.down('sm')]: { padding: '8px 12px 6px 8px', gap: 2 },
}));

/** As wide as the widest month, "September 2026", so the arrows beside it never move. */
const Title = styled('h1')(({ theme }) => ({
  margin: '0 2px',
  minWidth: '8em',
  textAlign: 'center',
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: 34,
  lineHeight: 1.1,
  letterSpacing: '-0.01em',
  color: BRAND.parchment,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: { fontSize: 28 },
  [theme.breakpoints.down('sm')]: { fontSize: 21 },
}));

const Arrow = styled('button')(({ theme }) => ({
  appearance: 'none',
  width: 34,
  height: 34,
  flexShrink: 0,
  display: 'grid',
  placeItems: 'center',
  border: 0,
  borderRadius: 999,
  background: 'transparent',
  color: BRAND.parchment,
  fontSize: 24,
  lineHeight: 1,
  cursor: 'pointer',
  '&:hover': { background: 'rgba(243,238,231,0.1)' },
  '&:focus-visible': { outline: `2px solid ${BRAND.parchment}` },
  [theme.breakpoints.down('sm')]: { width: 30, height: 30 },
}));

const TodayButton = styled('button')(({ theme }) => ({
  appearance: 'none',
  marginLeft: 8,
  height: 30,
  padding: '0 14px',
  flexShrink: 0,
  borderRadius: 999,
  border: '1px solid rgba(243,238,231,0.45)',
  background: 'transparent',
  color: BRAND.parchment,
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  '&:hover': { borderColor: BRAND.parchment, background: 'rgba(243,238,231,0.08)' },
  '&:disabled': { opacity: 0.35, cursor: 'default', background: 'transparent' },
  [theme.breakpoints.down('sm')]: { margin: '0 6px 0 auto', height: 28, padding: '0 10px', fontSize: 12 },
}));

/** The month and, when it is empty, the note over it. */
const MonthWrap = styled('div')({ position: 'relative' });

const EmptyNote = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(440px, calc(100% - 32px))',
  padding: '24px 26px 22px',
  background: BRAND.parchment,
  color: BRAND.mocha,
  textAlign: 'center',
  ...notchMask({ radius: 12 }),
  [theme.breakpoints.down('sm')]: {
    position: 'static',
    transform: 'none',
    width: 'auto',
    margin: '18px 16px 0',
  },
}));

const EmptyText = styled('p')({
  margin: '0 0 16px',
  fontFamily: FONTS.serif,
  fontSize: 19,
  fontWeight: 700,
  lineHeight: 1.3,
});

const PillButton = styled('button', { shouldForwardProp: (p) => p !== '$quiet' })<{ $quiet?: boolean }>(
  ({ $quiet }) => ({
    appearance: 'none',
    height: 36,
    padding: '0 20px',
    borderRadius: 999,
    border: `1px solid ${BRAND.mocha}`,
    background: $quiet ? 'transparent' : BRAND.mocha,
    color: $quiet ? BRAND.mocha : BRAND.parchment,
    fontFamily: FONTS.body,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    margin: 4,
  }),
);

const DayHead = styled('h2')({
  margin: 0,
  padding: '18px 16px 2px',
  display: 'flex',
  alignItems: 'baseline',
  gap: 12,
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: 22,
  color: BRAND.parchment,
  '& span': { fontWeight: 400, fontSize: 16, color: 'rgba(243,238,231,0.72)' },
});

const DayNote = styled('p')({
  margin: 0,
  padding: '10px 16px 0',
  fontFamily: FONTS.body,
  fontSize: 14,
  color: 'rgba(243,238,231,0.65)',
});

/** "Go to Saved" from a toast or the callout: the day the event landed on. `n` makes a repeat count. */
export interface ScheduleFocus {
  dayKey: string;
  n: number;
}

interface Props {
  scope: Scope;
  /** Every matching date, not one per series: a weekly program shows on each of its days. */
  events: Opportunity[];
  statusOf: (id: string) => RsvpStatus | null;
  now: Moment;
  /** The event whose callout is open, ringed so it can be found again. */
  activeId: string | null;
  focus: ScheduleFocus | null;
  /** The results bar, at the top of the scroll above the month. */
  top?: ReactNode;
  /** "Explore NYC", on Saved's empty month. */
  cityShort: string;
  /** "Drop Outdoor": offered on an empty month when a filter is what emptied the whole calendar. */
  suggestion: LoosenSuggestion | null;
  onLoosen: (s: LoosenSuggestion) => void;
  onExplore: () => void;
  onOpen: (event: Opportunity) => void;
  onToggleRsvp: (id: string, status: RsvpStatus) => void;
  /** The scroll, for its remembered position. */
  scrollRef?: Ref<HTMLDivElement>;
  onScroll?: UIEventHandler<HTMLDivElement>;
}

export default function CalendarView({
  scope,
  events,
  statusOf,
  now,
  activeId,
  focus,
  top,
  cityShort,
  suggestion,
  onLoosen,
  onExplore,
  onOpen,
  onToggleRsvp,
  scrollRef,
  onScroll,
}: Props) {
  const phone = useIsMobile();
  const today = now.dayKey;

  const days = useMemo(() => bucketByDay(events), [events]);

  const [month, setMonth] = useState<Month>(() => monthOf(focus?.dayKey ?? today));
  /** The phone's chosen day; null is the month's default. */
  const [picked, setPicked] = useState<string | null>(focus?.dayKey ?? null);
  const [seenFocus, setSeenFocus] = useState(focus);
  if (focus !== seenFocus) {
    setSeenFocus(focus);
    if (focus) {
      setMonth(monthOf(focus.dayKey));
      setPicked(focus.dayKey);
    }
  }
  const goTo = (m: Month) => {
    setMonth(m);
    setPicked(null);
  };
  const selected = picked && inMonth(picked, month) ? picked : defaultDay(month, today, days);
  const selectDay = (day: string) => {
    if (!inMonth(day, month)) setMonth(monthOf(day));
    setPicked(day);
  };

  const thisMonth = monthKey(month) === today.slice(0, 7);
  const monthEmpty = ![...days.keys()].some((k) => inMonth(k, month));
  const elsewhere = monthEmpty ? nearestBusyMonth(month, days) : null;

  const bar = (
    <Bar>
      <Arrow type="button" aria-label="Previous month" onClick={() => goTo(shiftMonth(month, -1))}>
        ‹
      </Arrow>
      <Title aria-live="polite">{monthTitle(month)}</Title>
      <Arrow type="button" aria-label="Next month" onClick={() => goTo(shiftMonth(month, 1))}>
        ›
      </Arrow>
      {!phone && (
        <TodayButton type="button" disabled={thisMonth} onClick={() => goTo(monthOf(today))}>
          Today
        </TodayButton>
      )}
      {phone && !thisMonth && (
        <TodayButton type="button" onClick={() => goTo(monthOf(today))}>
          Today
        </TodayButton>
      )}
    </Bar>
  );

  const emptyNote = monthEmpty && (
    <EmptyNote>
      <EmptyText>
        {scope === 'saved'
          ? 'Nothing in Saved this month. Mark events Going or Maybe in Explore.'
          : 'No events match this month. Try another month or loosen a filter.'}
      </EmptyText>
      {scope === 'saved' && (
        <PillButton type="button" onClick={onExplore}>
          Explore {cityShort}
        </PillButton>
      )}
      {suggestion && (
        <PillButton type="button" $quiet={scope === 'saved'} onClick={() => onLoosen(suggestion)}>
          Drop {suggestion.label}
        </PillButton>
      )}
      {elsewhere && (
        <PillButton type="button" $quiet onClick={() => goTo(elsewhere)}>
          {monthName(elsewhere)} ›
        </PillButton>
      )}
    </EmptyNote>
  );

  const dayEvents = days.get(selected) ?? [];
  const { full, weekday } = longDate(selected);

  const body = phone ? (
    <>
      {top}
      {bar}
      <div style={{ padding: '0 8px' }}>
        <DotCalendar
          month={month}
          today={today}
          days={days}
          statusOf={statusOf}
          selected={selected}
          onSelect={selectDay}
        />
      </div>
      {monthEmpty ? (
        emptyNote
      ) : (
        <section aria-label={`${weekday}, ${full}`}>
          <DayHead>
            {weekday}
            <span>{full}</span>
          </DayHead>
          {!dayEvents.length && <DayNote>Nothing on this day. Tap a day with a dot.</DayNote>}
          {dayEvents.map((event, i) => (
            <div key={event.id} style={{ marginTop: i ? 0 : 6 }}>
              <MobileEventCard
                event={event}
                rank={i}
                scheduled={statusOf(event.id) === 'going'}
                saved={statusOf(event.id) === 'maybe'}
                savedView={scope === 'saved'}
                onOpen={onOpen}
                onToggleSchedule={(id) => onToggleRsvp(id, 'going')}
                onToggleSave={(id) => onToggleRsvp(id, 'maybe')}
                past={isPastEvent(event, now)}
              />
            </div>
          ))}
        </section>
      )}
    </>
  ) : (
    <Inner>
      {top}
      {bar}
      <MonthWrap>
        <div style={monthEmpty ? { opacity: 0.45 } : undefined} inert={monthEmpty || undefined}>
          <MonthCalendar
            key={monthKey(month)}
            month={month}
            today={today}
            days={days}
            statusOf={statusOf}
            activeId={activeId}
            onOpen={onOpen}
          />
        </div>
        {emptyNote}
      </MonthWrap>
    </Inner>
  );

  return (
    <Scroll ref={scrollRef} onScroll={onScroll}>
      {body}
    </Scroll>
  );
}
